import type { ReactNode } from "react"
import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconEyeOutline18 as Eye,
	IconEyeSlashOutline18 as EyeOff,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	AlertDialog,
	IconButton,
	NucleoFillIcon,
	Pill,
	Tooltip,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"

import { RoleCheck } from "@/components/shared/role-check"
import { analytics } from "@/lib/analytics"
import { mutateOk, useZero } from "@/lib/zero"

interface HelpCenterSubpageCardProps {
	article: {
		id: string
		title: string
		summary: string | null
		icon: string
		published: boolean
	}
	titleComponent?: ReactNode
	summaryComponent?: ReactNode
}

export function HelpCenterSubpageCard({
	article,
	titleComponent,
	summaryComponent,
}: HelpCenterSubpageCardProps) {
	const navigate = useNavigate()
	const zero = useZero()
	const [isDeleteOpen, setIsDeleteOpen] = useState(false)

	return (
		<>
			<AlertDialog
				isOpen={isDeleteOpen}
				onOpenChange={setIsDeleteOpen}
				confirmText="Delete"
				title={
					article.title ? `Delete "${article.title}"?` : "Delete this subpage?"
				}
				description={
					article.title
						? `Are you sure you want to delete the subpage "${article.title}"?`
						: "Are you sure you want to delete this subpage?"
				}
				onCancel={() => setIsDeleteOpen(false)}
				onConfirm={() => {
					zero.mutate(mutators.helpCenterArticles.delete({ id: article.id }))
					analytics.track("helpcenterarticle-subpage-delete", {
						id: article.id,
					})
					setIsDeleteOpen(false)
				}}
			/>
			<div
				role="button"
				tabIndex={0}
				onClick={() =>
					void navigate({ to: "/edit-docs/$id", params: { id: article.id } })
				}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault()
						void navigate({ to: "/edit-docs/$id", params: { id: article.id } })
					}
				}}
				className={cn(
					"group relative flex cursor-default flex-col overflow-hidden rounded-lg bg-black/[0.02] px-3 py-4 ring-1 ring-black/15 hover:bg-gray-100 lg:px-[1.1rem] dark:bg-gray-850 dark:shadow-black dark:ring-white/10 dark:hover:bg-gray-800",
					!article.published && "opacity-60",
				)}
			>
				<div className="mb-2 flex items-center justify-between">
					<div className="flex h-6 w-6 items-center justify-center overflow-hidden">
						<NucleoFillIcon
							icon={article.icon}
							className="size-[18px] min-h-[18px] min-w-[18px] text-primary"
						/>
					</div>
					<div className="flex items-center gap-1">
						{!article.published && (
							<Tooltip label="This subpage is not yet published">
								<Pill size="sm">Draft</Pill>
							</Tooltip>
						)}
						<RoleCheck not={["VIEWER"]}>
							<DropdownMenu
								contentProps={{
									align: "end",
									className: "w-fit pr-1",
								}}
								trigger={
									<IconButton
										Icon={MoreVertical}
										variant="ghost"
										size="md"
										className="relative opacity-0 hover:bg-gray-200 group-hover:opacity-100"
										type="button"
										onClick={(e) => e.stopPropagation()}
									/>
								}
							>
								<DropdownMenuItem
									Icon={article.published ? EyeOff : Eye}
									onClick={async (e) => {
										e.stopPropagation()
										await mutateOk(
											zero.mutate(
												mutators.helpCenterArticles.update({
													id: article.id,
													published: !article.published,
												}),
											),
										)
										analytics.track(
											article.published
												? "helpcenterarticle-unpublish"
												: "helpcenterarticle-publish",
											{ id: article.id },
										)
									}}
								>
									{article.published ? "Unpublish" : "Publish"}
								</DropdownMenuItem>
								<DropdownMenuItem
									Icon={Trash}
									onClick={(e) => {
										e.stopPropagation()
										setIsDeleteOpen(true)
									}}
								>
									Delete
								</DropdownMenuItem>
							</DropdownMenu>
						</RoleCheck>
					</div>
				</div>
				<h3 className="mt-4 line-clamp-1 min-w-0 text-ellipsis text-[0.95rem] font-medium tracking-tight text-primary">
					{titleComponent ?? article.title ?? "New article"}
				</h3>
				{(summaryComponent || article.summary) && (
					<p className="mt-1 line-clamp-2 min-w-0 overflow-hidden text-ellipsis text-[0.875rem] tracking-tight text-gray-600 opacity-90 dark:text-gray-400">
						{summaryComponent || article.summary}
					</p>
				)}
			</div>
		</>
	)
}
